import produce from 'immer';
import { uniqBy } from 'lodash';
import { handleActions } from 'redux-actions';
import { RECOMMEND_SEARCH_WORD_TYPE } from '../../../../constants/service/app/recommendSearchWordConstants';
import { setUncheckableTableTransferList, updateStoreWithPath } from '../../../../lib/common';
import { _LIST_PAGINATION_MODEL } from '../../app/models';
import actions from './actions';
import {
  RECOMMEND_SEARCH_WORD_LIST_SEARCH_DATA_MODEL,
  RECOMMEND_SEARCH_WORD_LIST_SEARCH_OPTION_MODEL,
  RECOMMEND_SEARCH_WORD_SAVE_MODEL,
} from './models';

/**
 *  INITIAL STATE
 */
const initialState = {
  /**
   *  추천 검색 키워드 리스트
   */
  recommendSearchWordList: {
    /**
     *  리스트
     */
    list: [],
    /**
     *  검색 박스
     */
    searchData: RECOMMEND_SEARCH_WORD_LIST_SEARCH_DATA_MODEL,
    /**
     *  검색 옵션
     */
    searchOption: RECOMMEND_SEARCH_WORD_LIST_SEARCH_OPTION_MODEL,
    /**
     *  리스트 pagination
     */
    pagination: _LIST_PAGINATION_MODEL,
  },

  /**
   *  추천 검색 키워드 상세
   */
  recommendSearchWordDetail: {
    /**
     *  추천 검색 키워드 정보
     */
    recommendSearchWordInfo: {
      /**
       *  추천 검색 키워드 식별 id
       */
      recommendSearchWordId: null,
      /**
       *  추천인
       */
      adminName: null,
      /**
       *  추천 검색 키워드 타입
       */
      type: RECOMMEND_SEARCH_WORD_TYPE.POPULAR_POP,
      /**
       *  추천 검색 키워드 생성일
       */
      createDate: null,
      /**
       *  메모
       */
      memo: null,
    },
    /**
     *  추천 검색 키워드 아이템 리스트
     */
    recommendSearchWordItemList: [],
  },

  /**
   *  추천 검색 키워드 등록/복사
   */
  recommendSearchWordSave: RECOMMEND_SEARCH_WORD_SAVE_MODEL,
};

/**
 *  REDUCER
 */
export default handleActions(
  {
    /**
     *  추천 검색 키워드 데이터 변경하기 성공
     */
    [actions.SET_DATA]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath(action.payload.path, action.payload.value, draft);
      });
    },

    /**
     *  추천 검색 키워드 리스트 가져오기 성공
     */
    [actions.GET_RECOMMEND_SEARCH_WORD_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.recommendSearchWordList = {
          ...draft.recommendSearchWordList,
          ...action.payload.recommendSearchWordList,
        };
      });
    },

    /**
     *  추천 검색 키워드 상세 요청하기 성공
     */
    [actions.GET_RECOMMEND_SEARCH_WORD_DETAIL_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.recommendSearchWordDetail = action.payload.recommendSearchWordDetail;
      });
    },

    /**
     *  추천 검색 키워드 복사하기 전 필요한 데이터 가져오기 성공
     */
    [actions.PREPARE_COPY_RECOMMEND_SEARCH_WORD_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택할 검색 키워드 리스트
         */
        draft.recommendSearchWordSave.recommendSearchWordItemList.itemList = {
          ...draft.recommendSearchWordSave.recommendSearchWordItemList.itemList,
          ...action.payload.recommendSearchWordSave.recommendSearchWordItemList,
        };

        /**
         *  검색 키워드 정보
         */
        draft.recommendSearchWordSave.recommendSearchWordInfo =
          action.payload.recommendSearchWordSave.recommendSearchWordInfo;

        /**
         *  선택된 검색 키워드 리스트
         */
        draft.recommendSearchWordSave.recommendSearchWordItemList.selectedItemList = {
          list: action.payload.recommendSearchWordSave.selectedRecommendSearchWordItemList,
          idList: action.payload.recommendSearchWordSave.selectedRecommendSearchWordItemList.map((item) => item.id),
        };

        /**
         *  선택할 검색 키워드 아이템 중에 이미 설정된 검색 키워드 아이템인 경우에는
         *  검색 키워드 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.recommendSearchWordSave.recommendSearchWordItemList.itemList.list = setUncheckableTableTransferList({
          list: draft.recommendSearchWordSave.recommendSearchWordItemList.itemList.list,
          selectedIdList: draft.recommendSearchWordSave.recommendSearchWordItemList.selectedItemList.idList,
        });
      });
    },

    /**
     *  추천 검색 키워드 등록하기 전 필요한 데이터 가져오기 성공
     */
    [actions.PREPARE_CREATE_RECOMMEND_SEARCH_WORD_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  검색 키워드 리스트
         */
        draft.recommendSearchWordSave.recommendSearchWordItemList.itemList =
          action.payload.recommendSearchWordSave.recommendSearchWordItemList;
      });
    },

    /**
     *  추천 검색 키워드에 연결할 아이템 리스트 요청하기 성공
     */
    [actions.GET_RECOMMEND_SEARCH_WORD_ITEM_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택할 검색 키워드 리스트
         */
        draft.recommendSearchWordSave.recommendSearchWordItemList.itemList = {
          ...draft.recommendSearchWordSave.recommendSearchWordItemList.itemList,
          ...action.payload.recommendSearchWordItemList,
        };

        /**
         *  선택할 검색 키워드 아이템 중에 이미 설정된 검색 키워드 아이템인 경우에는
         *  검색 키워드 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.recommendSearchWordSave.recommendSearchWordItemList.itemList.list = setUncheckableTableTransferList({
          list: draft.recommendSearchWordSave.recommendSearchWordItemList.itemList.list,
          selectedIdList: draft.recommendSearchWordSave.recommendSearchWordItemList.selectedItemList.idList,
        });
      });
    },

    /**
     *  검색 키워드 리스트 EXCEL 업로드 성공
     */
    [actions.SET_RECOMMEND_SEARCH_WORD_ITEM_LIST_EXCEL_UPLOAD_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택된 검색 키워드 리스트
         */
        draft.recommendSearchWordSave.recommendSearchWordItemList.selectedItemList.list = action.payload.itemList;

        /**
         *  선택된 검색 키워드 아이템의 중복 제거하기
         */
        draft.recommendSearchWordSave.recommendSearchWordItemList.selectedItemList.list = uniqBy(
          draft.recommendSearchWordSave.recommendSearchWordItemList.selectedItemList.list,
          'id',
        );

        draft.recommendSearchWordSave.recommendSearchWordItemList.selectedItemList.idList =
          draft.recommendSearchWordSave.recommendSearchWordItemList.selectedItemList.list.map((item) => item.id);

        /**
         *  선택할 검색 키워드 아이템 중에 이미 설정된 검색 키워드 아이템인 경우에는
         *  검색 키워드 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.recommendSearchWordSave.recommendSearchWordItemList.itemList.list = setUncheckableTableTransferList({
          list: draft.recommendSearchWordSave.recommendSearchWordItemList.itemList.list,
          selectedIdList: draft.recommendSearchWordSave.recommendSearchWordItemList.selectedItemList.idList,
        });
      });
    },
  },
  initialState,
);
